import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { getUnserviceableAreaRequest } from '../../actions/unserviceableAreaRequest';
import { 
    ArrowLeft, 
    MapPin, 
    User, 
    Calendar, 
    Navigation,
    Globe,
    Map,
    AlertTriangle
} from 'lucide-react';

const UnserviceableAreaRequest = ({ getUnserviceableAreaRequest, unserviceableAreaRequest, loading }) => {
    const { id } = useParams();

    useEffect(() => {
        if (id) {
            getUnserviceableAreaRequest(id);
        }
    }, [id]);

    const openInGoogleMaps = (coordinates) => {
        const googleMapsUrl = process.env.REACT_APP_GOOGLE_MAPS_URL || 'https://www.google.com/maps';
        const latitude = coordinates[0];
        const longitude = coordinates[1];
        const mapsUrl = `${googleMapsUrl}?q=${latitude},${longitude}`;
        window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading unserviceable area request details...</p>
                </div>
            </div>
        );
    }

    if (!unserviceableAreaRequest) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <MapPin size={64} className="text-muted mb-3" />
                    <h4 className="text-muted mb-3">Unserviceable Area Request Not Found</h4>
                    <p className="text-muted mb-4">The requested unserviceable area request could not be found.</p>
                    <Link to="/inventory/v1.2/unserviceable-area-requests" className="btn btn-primary">
                        <ArrowLeft size={18} className="me-2" />
                        Back to Requests
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex align-items-center mb-3">
                            <Link 
                                to="/inventory/v1.2/unserviceable-area-requests"
                                className="btn btn-outline-secondary me-3"
                            >
                                <ArrowLeft size={18} className="me-2" />
                                Back to Requests
                            </Link>
                            <div className="flex-grow-1">
                                <h1 className="h3 mb-2 text-gray-800 fw-bold">
                                    <MapPin size={32} className="me-3 text-primary" />
                                    Unserviceable Area Request Details
                                </h1>
                                <p className="text-muted mb-0">
                                    Request ID: {unserviceableAreaRequest._id}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Main Content */}
                    <div className="col-lg-8">
                        {/* Request Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="card-title mb-0">
                                    <MapPin size={20} className="me-2 text-primary" />
                                    Request Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <User size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Requested By</div>
                                                <div className="fw-bold">
                                                    {unserviceableAreaRequest.user?.name || 'Unknown User'}
                                                </div>
                                                {unserviceableAreaRequest.user?._id && (
                                                    <div className="small text-muted">
                                                        User ID: {unserviceableAreaRequest.user._id}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Calendar size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Request Date</div>
                                                <div className="fw-bold">
                                                    {new Date(unserviceableAreaRequest.createdAt).toLocaleDateString()}
                                                </div>
                                                <div className="small text-muted">
                                                    {new Date(unserviceableAreaRequest.createdAt).toLocaleTimeString()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Location Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="card-title mb-0">
                                    <Navigation size={20} className="me-2 text-primary" />
                                    Location Information
                                </h5>
                            </div>
                            <div className="card-body">
                                {unserviceableAreaRequest.location && unserviceableAreaRequest.location.coordinates ? (
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="d-flex align-items-start">
                                                <MapPin size={18} className="text-muted me-3 mt-1" />
                                                <div className="flex-grow-1">
                                                    <div className="small text-muted">GPS Coordinates</div>
                                                    <div className="fw-bold h5 mb-2">
                                                            {unserviceableAreaRequest.location.coordinates[0]}, {unserviceableAreaRequest.location.coordinates[1]}
                                                    </div>
                                                    <div className="small text-muted mb-3">
                                                        Longitude: {unserviceableAreaRequest.location.coordinates[0]} | 
                                                        Latitude: {unserviceableAreaRequest.location.coordinates[1]}
                                                    </div>
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => openInGoogleMaps(unserviceableAreaRequest.location.coordinates)}
                                                    >
                                                        <Globe size={16} className="me-2" />
                                                        View on Google Maps
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {unserviceableAreaRequest.location.type && (
                                            <div className="col-12">
                                                <div className="d-flex align-items-center">
                                                    <Navigation size={18} className="text-muted me-3" />
                                                    <div>
                                                        <div className="small text-muted">Location Type</div>
                                                        <span className="badge bg-info fs-6">
                                                            {unserviceableAreaRequest.location.type}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <MapPin size={48} className="text-muted mb-3" />
                                        <p className="text-muted">No location information available for this request.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="col-lg-4">
                        {/* Request Details */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h6 className="card-title mb-0">Request Details</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="small text-muted">Request ID</div>
                                    <div className="fw-bold small">{unserviceableAreaRequest._id}</div>
                                </div>
                                <div className="mb-3">
                                    <div className="small text-muted">Created</div>
                                    <div className="fw-bold">
                                        {new Date(unserviceableAreaRequest.createdAt).toLocaleString()}
                                    </div>
                                </div>
                                {unserviceableAreaRequest.updatedAt && (
                                    <div className="mb-3">
                                        <div className="small text-muted">Last Updated</div>
                                        <div className="fw-bold">
                                            {new Date(unserviceableAreaRequest.updatedAt).toLocaleString()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status Card */}
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <h6 className="card-title mb-0">Request Status</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <AlertTriangle size={18} className="text-warning me-2" />
                                        <span className="fw-bold">Unserviceable Area</span>
                                    </div>
                                    <div className="small text-muted mt-1">
                                        This location is currently not serviceable by the delivery system.
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <Map size={18} className="text-info me-2" />
                                        <span className="fw-bold">Location Based</span>
                                    </div>
                                    <div className="small text-muted mt-1">
                                        Request is tied to specific geographic coordinates.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

UnserviceableAreaRequest.propTypes = {
    getUnserviceableAreaRequest: PropTypes.func.isRequired,
    unserviceableAreaRequest: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    unserviceableAreaRequest: state.unserviceableAreaRequest.unserviceableAreaRequest,
    loading: state.unserviceableAreaRequest.loading
});

export default connect(mapStateToProps, { getUnserviceableAreaRequest })(UnserviceableAreaRequest);
